"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { SitePlanEditor, SitePlanPrint, useSitePlan } from "@/components/tools/SitePlanCanvas";
import {
  Field,
  TextInput,
  TextArea,
  SelectInput,
  CheckboxRow,
  GuardedPrintButton,
  CompletionPanel,
  PrintFooter,
  trackToolResult,
  EvidenceFields,
  EvidencePrint,
  evidenceRequiredItems,
  useSavedEvidence,
  type Evidence,
} from "@/components/tools/ui";

/* Construction phase plan (CPP) builder — the web-native replacement for HSE's
   retired CDM Wizard, aimed at small and domestic projects.

   Domain grounding (RamsDocs knowledge bank + HSE CDM 2015 pages): under CDM 2015
   a written construction phase plan must be in place BEFORE construction work
   starts on EVERY project, regardless of size, duration or whether the client is
   domestic or commercial (Regulation 12, applied to single-contractor projects
   via Regulation 15). On single-contractor jobs the contractor prepares it; where
   more than one contractor is involved a principal contractor must be appointed to
   draw it up and keep it current. The plan must be PROPORTIONATE — a short loft
   conversion needs a few pages, not a binder. Required content is management
   arrangements (roles, site rules, welfare, emergency procedures, communication)
   plus the controls for the project's specific significant risks.

   This tool structures that document. It never declares a plan "CDM compliant" or
   "adequate" — proportionality and sufficiency are the competent person's call. */

interface RiskDef {
  id: string;
  /** Checkbox label shown in the form. */
  label: string;
  /** Short heading used in the printed plan. */
  heading: string;
  /** Tailored paragraph of standard controls written into the plan when ticked. */
  paragraph: string;
}

/* Each risk, when ticked, writes a heading + a standard-controls paragraph into
   the plan. Controls reflect HSE construction guidance; they are sensible starting
   points the responsible person must tailor to the actual site. */
const RISKS: RiskDef[] = [
  {
    id: "height",
    label: "Work at height (roofs, scaffolds, ladders, fragile surfaces)",
    heading: "Work at height",
    paragraph:
      "Work at height is avoided where reasonably practicable. Where it cannot be avoided, the right access equipment is selected for the task — scaffold or tower in preference to ladders for anything beyond short-duration, low-risk work. Scaffolds are erected, inspected and tagged by a competent person and re-inspected after alteration or adverse weather; towers are built and used to the manufacturer's instructions. Edge protection, guard rails or fall-arrest are provided as appropriate, fragile surfaces (roof lights, old roofs) are identified and protected, and the area below is cordoned to protect people from falling objects. Equipment is checked before use and the work is not carried out in high winds.",
  },
  {
    id: "excavation",
    label: "Excavations / digging / buried services",
    heading: "Excavations and buried services",
    paragraph:
      "Before any digging, buried services are located using up-to-date utility drawings, a cable avoidance tool (CAT) and hand-digging trial holes near known services. Excavations are supported, battered back or otherwise prevented from collapse, kept clear of stored spoil and plant at the edges, and provided with safe access and barriers/edge protection. They are inspected by a competent person at the start of each shift and after any event likely to affect stability. Where there is any risk of a confined-space atmosphere, a separate assessment and controls are applied before entry.",
  },
  {
    id: "asbestos",
    label: "Refurbishment / demolition of a building built or refurbished before 2000",
    heading: "Asbestos",
    paragraph:
      "The building predates the 2000 ban, so asbestos-containing materials must be presumed present until proven otherwise. A refurbishment/demolition asbestos survey is obtained and reviewed before intrusive work begins, and its findings are briefed to everyone on site. No work disturbs suspected ACMs until they are identified and managed; licensed work is carried out only by a licensed contractor. If unidentified suspect material is found during the works, work stops in that area, the area is isolated, and advice is sought before continuing.",
  },
  {
    id: "demolition",
    label: "Demolition or structural alterations (removing walls, beams, floors)",
    heading: "Demolition and structural alterations",
    paragraph:
      "Structural alterations and demolition are planned on the basis of a structural engineer's design and a demolition/alteration method, with the sequence of work and any temporary support agreed before work starts. Services are isolated, the structure is checked for instability, and work proceeds in a controlled top-down/agreed sequence. The exclusion of other trades and the public from the work zone is maintained throughout.",
  },
  {
    id: "tempsupport",
    label: "Temporary supports (props, needles, shoring, falsework)",
    heading: "Temporary supports and propping",
    paragraph:
      "Propping, needling, shoring and any falsework are designed or specified by a competent person, installed to that design, and not loaded or removed out of sequence. The supports are inspected before reliance and remain in place until the permanent structure can carry the load. Loadings, ground bearing and the condition of the existing structure are taken into account.",
  },
  {
    id: "hotworks",
    label: "Hot works (welding, grinding, blow-torch, roofing torches)",
    heading: "Hot works",
    paragraph:
      "Hot works are avoided where a cold alternative exists. Where unavoidable, a hot-work permit/checklist is used: combustibles are removed or protected, a suitable extinguisher is to hand, the area is checked before starting, and a fire watch is maintained during the work and for at least one hour afterwards. Gas equipment is checked for leaks and flashback arrestors are fitted; LPG is stored safely and not left in the building overnight.",
  },
  {
    id: "dust",
    label: "Dust / silica (cutting, grinding, chasing, sweeping)",
    heading: "Dust and respirable crystalline silica",
    paragraph:
      "Cutting, grinding and chasing of concrete, stone, brick and other silica-bearing materials generates respirable crystalline silica, which is controlled at source by water suppression or on-tool (M-class) extraction rather than relying on masks. Dry sweeping is avoided in favour of vacuuming or wet methods. Where residual exposure remains, suitable RPE (typically FFP3, face-fit tested for tight-fitting masks) is worn in addition to — not instead of — source control, and exposed workers receive health surveillance where required.",
  },
  {
    id: "manualhandling",
    label: "Manual handling (heavy / awkward loads, materials handling)",
    heading: "Manual handling",
    paragraph:
      "Manual handling is reduced by planning deliveries to the point of use, using wheelbarrows, trolleys, telehandlers or mechanical aids, and breaking loads down where possible. Heavy or awkward loads are team-lifted, and good handling technique is briefed. Routes are kept clear and level, and bagged materials are stored at a workable height to avoid repeated low lifting.",
  },
  {
    id: "electricity",
    label: "Electricity / live services",
    heading: "Electricity and live services",
    paragraph:
      "Existing services are identified and, wherever possible, isolated and proven dead before work begins, with locking-off and warning notices applied. Work on or near live electrical systems is avoided; where unavoidable it is carried out only by a competent person under a safe system of work. Site electrical supplies use RCD protection and 110V/battery tools in preference to 240V, and leads, tools and temporary installations are visually checked before use and PAT-tested in date.",
  },
  {
    id: "lifting",
    label: "Lifting operations (crane, hiab, telehandler, hoist)",
    heading: "Lifting operations",
    paragraph:
      "Lifting operations are planned by a competent person, with the right plant and in-date, marked lifting accessories for the load. Operators and slingers/signallers are trained and competent, the ground/setup is assessed for the plant, and the lift area is cordoned to keep people from under suspended loads. Plant and accessories are within their LOLER thorough-examination dates.",
  },
  {
    id: "plant",
    label: "Plant & site traffic (diggers, dumpers, deliveries, reversing)",
    heading: "Plant and site traffic",
    paragraph:
      "Pedestrians and moving plant/vehicles are kept apart by planned routes, segregation and clear sight lines. Operators are trained and competent, plant is maintained and fitted with the appropriate visibility aids, and reversing is minimised or controlled by a banksman. Delivery and collection times are managed to limit congestion and protect people on and around the site.",
  },
  {
    id: "occupied",
    label: "Occupied premises / public interface (people living or passing nearby)",
    heading: "Occupied premises and public protection",
    paragraph:
      "The site is separated from occupants and members of the public by physical barriers, secured access and clear signage. Work areas are isolated from the parts of the building still in use, shared routes are kept clean and safe, and noisy/dusty work is scheduled to limit disruption. Materials, plant and tools are secured or removed at the end of each day so the public — including children — cannot access them.",
  },
];

function formatLong(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

export function CppBuilder() {
  // 1. Project
  const [workDescription, setWorkDescription] = useState("");
  const [siteAddress, setSiteAddress] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientType, setClientType] = useState("domestic");
  const [startDate, setStartDate] = useState("");
  const [duration, setDuration] = useState("");
  const [maxPeople, setMaxPeople] = useState("");

  // 2. Management
  const [contractor, setContractor] = useState("");
  const [singleContractor, setSingleContractor] = useState(true);
  const [siteContact, setSiteContact] = useState("");
  const [subbieManagement, setSubbieManagement] = useState("");
  const [communication, setCommunication] = useState("");

  // 3. Arrangements
  const [welfare, setWelfare] = useState("");
  const [firstAid, setFirstAid] = useState("");
  const [induction, setInduction] = useState("");
  const [hours, setHours] = useState("");
  const [security, setSecurity] = useState("");
  const [housekeeping, setHousekeeping] = useState("");

  // 4. Key risks
  const [risks, setRisks] = useState<string[]>([]);

  // 5. Emergency
  const [fire, setFire] = useState("");
  const [escape, setEscape] = useState("");
  const [emergencyContacts, setEmergencyContacts] = useState("");
  const [nearestAe, setNearestAe] = useState("");

  // Document control & sign-off
  const [evidence, setEvidence, clearEvidence] = useSavedEvidence();
  const [sitePlan, setSitePlan] = useSitePlan("construction-phase-plan");

  const resultTracked = useRef(false);

  const toggleRisk = (id: string) =>
    setRisks((rs) => (rs.includes(id) ? rs.filter((r) => r !== id) : [...rs, id]));

  const selectedRisks = useMemo(
    () => RISKS.filter((r) => risks.includes(r.id)),
    [risks],
  );

  // The plan is "meaningful" once it describes the work — fire the funnel event then.
  const hasContent = workDescription.trim().length > 0;

  useEffect(() => {
    if (hasContent && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("construction-phase-plan", { risks: selectedRisks.length });
    }
  }, [hasContent, selectedRisks.length]);

  // Arrangement rows for the printed plan — only those with content are shown.
  const arrangements: { label: string; value: string }[] = [
    { label: "Welfare", value: welfare },
    { label: "First aid & accident reporting", value: firstAid },
    { label: "Site induction & briefing", value: induction },
    { label: "Hours of work", value: hours },
    { label: "Site security & public protection", value: security },
    { label: "Housekeeping & waste", value: housekeeping },
  ];
  const management: { label: string; value: string }[] = [
    {
      label: singleContractor ? "Contractor" : "Principal contractor",
      value: contractor,
    },
    { label: "Site contact / supervisor", value: siteContact },
    { label: "Managing & briefing subcontractors", value: subbieManagement },
    { label: "How the plan is communicated", value: communication },
  ];
  const emergency: { label: string; value: string }[] = [
    { label: "Fire arrangements & extinguishers", value: fire },
    { label: "Escape & assembly", value: escape },
    { label: "Emergency contacts", value: emergencyContacts },
    { label: "Nearest A&E", value: nearestAe },
  ];

  const clientTypeLabel = clientType === "commercial" ? "Commercial" : "Domestic";

  // The sections a CPP can't sensibly ship without — drives the sticky
  // progress panel and the print guard. Optional arrangements stay optional.
  const requiredItems: { label: string; done: boolean }[] = [
    { label: "Description of the work", done: workDescription.trim().length > 0 },
    { label: "Site address", done: siteAddress.trim().length > 0 },
    { label: "Client name", done: clientName.trim().length > 0 },
    { label: singleContractor ? "Contractor" : "Principal contractor", done: contractor.trim().length > 0 },
    { label: "Welfare arrangements", done: welfare.trim().length > 0 },
    { label: "First aid & accident reporting", done: firstAid.trim().length > 0 },
    { label: "Fire arrangements & extinguishers", done: fire.trim().length > 0 },
    { label: "Escape & assembly", done: escape.trim().length > 0 },
    { label: "Emergency contacts", done: emergencyContacts.trim().length > 0 },
    ...evidenceRequiredItems(evidence),
  ];
  const missingRequired = requiredItems.filter((i) => !i.done).map((i) => i.label);

  return (
    <div className="space-y-6">
      {/* Sticky progress + next gap, so long scrolls keep their bearings. */}
      <div className="sticky top-20 z-10">
        <CompletionPanel items={requiredItems} title="Plan completion" />
      </div>

      {/* Proportionality prompt */}
      <div className="rounded-xl border border-emerald/30 bg-emerald-tint px-5 py-4 text-[0.92rem] leading-relaxed text-emerald-deep">
        <strong>Keep it proportionate.</strong> A construction phase plan is required before work starts on
        every project under CDM 2015 — but for a small or domestic job that means a few clear pages, not a
        binder. Fill in what applies to your project and leave the rest. Sections you leave blank are left out
        of the printed plan.
      </div>

      {/* 1. Project */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">1. The project</h2>
        <div className="mt-4 grid gap-4">
          <Field label="Description of the work">
            <TextArea
              value={workDescription}
              onChange={(e) => setWorkDescription(e.target.value)}
              placeholder="e.g. Single-storey rear extension: groundworks, blockwork, timber roof, internal fit-out."
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Site address">
              <TextInput value={siteAddress} onChange={(e) => setSiteAddress(e.target.value)} placeholder="Full site address" />
            </Field>
            <Field label="Client name">
              <TextInput value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Who the work is for" />
            </Field>
            <Field label="Client type" hint="Domestic clients have most CDM duties carried by the contractor.">
              <SelectInput value={clientType} onChange={(e) => setClientType(e.target.value)}>
                <option value="domestic">Domestic (homeowner)</option>
                <option value="commercial">Commercial / business</option>
              </SelectInput>
            </Field>
            <Field label="Start date">
              <TextInput type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Field>
            <Field label="Expected duration">
              <TextInput value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="e.g. 6 weeks" />
            </Field>
            <Field label="Max people on site at once">
              <TextInput value={maxPeople} onChange={(e) => setMaxPeople(e.target.value)} placeholder="e.g. 4" />
            </Field>
          </div>
        </div>
      </div>

      {/* 2. Management */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">2. Management of the work</h2>
        <div className="mt-4 grid gap-4">
          <div className="rounded-lg border border-line bg-mist/60 p-4">
            <CheckboxRow checked={singleContractor} onChange={setSingleContractor}>
              I am the only contractor on this job (no other contractors involved)
            </CheckboxRow>
            <p className="mt-2 text-[0.82rem] leading-relaxed text-muted">
              On a single-contractor project you prepare the plan. Where more than one contractor is involved, a
              <strong> principal contractor</strong> must be appointed to draw it up and keep it current.
            </p>
          </div>
          <Field label={singleContractor ? "Contractor" : "Principal contractor"}>
            <TextInput value={contractor} onChange={(e) => setContractor(e.target.value)} placeholder="Company / person responsible for managing the work" />
          </Field>
          <Field label="Named site contact / supervisor">
            <TextInput value={siteContact} onChange={(e) => setSiteContact(e.target.value)} placeholder="Name & phone number of the person in charge on site" />
          </Field>
          <Field
            label="How subcontractors are managed & briefed"
            hint="Leave blank if there are no subcontractors."
          >
            <TextArea
              value={subbieManagement}
              onChange={(e) => setSubbieManagement(e.target.value)}
              placeholder="e.g. Subbies checked for competence/insurance, given the plan and RAMS, briefed before starting, supervised by the site contact."
            />
          </Field>
          <Field label="How the plan is communicated">
            <TextArea
              value={communication}
              onChange={(e) => setCommunication(e.target.value)}
              placeholder="e.g. Plan briefed at induction, a copy kept on site, changes re-briefed to everyone affected."
            />
          </Field>
        </div>
      </div>

      {/* 3. Arrangements */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">3. Site arrangements</h2>
        <div className="mt-4 grid gap-4">
          <Field
            label="Welfare (toilet, washing, rest)"
            hint="Required even on domestic jobs — e.g. use of the house WC by arrangement, hand-washing, a place to take breaks."
          >
            <TextArea value={welfare} onChange={(e) => setWelfare(e.target.value)} placeholder="e.g. Toilet and hand-washing in the house by agreement; mess/rest area in the garage; drinking water on site." />
          </Field>
          <Field label="First aid & accident reporting">
            <TextArea value={firstAid} onChange={(e) => setFirstAid(e.target.value)} placeholder="e.g. Stocked first-aid kit on site; trained first aider named; injuries recorded; RIDDOR-reportable events reported to HSE." />
          </Field>
          <Field label="Site induction & briefing">
            <TextArea value={induction} onChange={(e) => setInduction(e.target.value)} placeholder="e.g. Everyone inducted before first starting: hazards, welfare, emergency arrangements, site rules." />
          </Field>
          <Field label="Hours of work">
            <TextInput value={hours} onChange={(e) => setHours(e.target.value)} placeholder="e.g. Mon–Fri 08:00–17:00; no noisy work before 08:00" />
          </Field>
          <Field label="Site security & public protection">
            <TextArea value={security} onChange={(e) => setSecurity(e.target.value)} placeholder="e.g. Heras fencing / hoarding; locked access; warning signage; site secured and plant immobilised out of hours." />
          </Field>
          <Field label="Housekeeping & waste">
            <TextArea value={housekeeping} onChange={(e) => setHousekeeping(e.target.value)} placeholder="e.g. Routes kept clear; skip on site; waste segregated; daily tidy-up; trip hazards removed." />
          </Field>
        </div>
      </div>

      {/* 4. Key risks */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">4. Key risks &amp; how they are controlled</h2>
        <p className="mt-1 text-sm text-muted">
          Tick the significant risks for this project. Each adds a section of standard controls to the plan —
          edit and add to them so they describe what you will actually do on this site.
        </p>
        <div className="mt-4 space-y-2">
          {RISKS.map((r) => (
            <CheckboxRow key={r.id} checked={risks.includes(r.id)} onChange={() => toggleRisk(r.id)}>
              {r.label}
            </CheckboxRow>
          ))}
        </div>
      </div>

      {/* 5. Emergency */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">5. Emergency arrangements</h2>
        <div className="mt-4 grid gap-4">
          <Field label="Fire arrangements & extinguishers">
            <TextArea value={fire} onChange={(e) => setFire(e.target.value)} placeholder="e.g. Suitable extinguisher(s) on site; ignition sources controlled; hot-work permit; no smoking; flammables stored safely." />
          </Field>
          <Field label="Escape & assembly">
            <TextArea value={escape} onChange={(e) => setEscape(e.target.value)} placeholder="e.g. Escape routes kept clear; assembly point at [location]; occupants alerted in shared premises." />
          </Field>
          <Field label="Emergency contacts">
            <TextArea value={emergencyContacts} onChange={(e) => setEmergencyContacts(e.target.value)} placeholder="e.g. Site contact, client, utility emergency lines, 999/112." />
          </Field>
          <Field
            label="Nearest A&E"
            hint="Tip: use the Nearest A&E finder to get the hospital, directions and what to record."
          >
            <TextArea value={nearestAe} onChange={(e) => setNearestAe(e.target.value)} placeholder="Nearest A&E hospital name, address and route from site." />
          </Field>
          <p className="rounded-lg border border-line bg-mist/60 px-4 py-3 text-[0.85rem] leading-relaxed text-body">
            Need the emergency block?{" "}
            <a href="/tools/nearest-ae-finder/" className="font-semibold text-emerald-deep underline">
              Use the free Nearest A&amp;E finder
            </a>{" "}
            to look up the closest A&amp;E to the site postcode, then paste it above.
          </p>
        </div>
      </div>

      <SitePlanEditor
        value={sitePlan}
        onChange={setSitePlan}
        title="Site layout sketch"
        hint="Sketch the site set-up: boundary, access, welfare, storage, routes and any exclusion zones. The sketch prints with the construction phase plan."
      />

      {/* Document control & sign-off */}
      <EvidenceFields
        value={evidence}
        onChange={setEvidence}
        intro="A construction phase plan is a living document — update it and bump the revision when the work, people or risks change, and have it reviewed by a competent person before work starts."
      />

      <GuardedPrintButton
        tool="construction-phase-plan"
        label="Print / save plan as PDF"
        missing={missingRequired}
        warningTitle="This construction phase plan still has empty core sections"
      />

      {/* Printable construction phase plan */}
      <div className="print-area card shadow-soft p-6" id="cpp-document">
        <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
          Construction phase plan (CDM 2015)
        </div>
        <h2 className="mt-1 text-lg">
          {workDescription.trim() ? workDescription.trim() : "Construction phase plan"}
        </h2>
        <p className="mt-1 text-[0.82rem] text-muted">
          {siteAddress || "Site address not stated"}
        </p>

        {/* Project table */}
        <table className="mt-4 w-full text-sm">
          <tbody>
            <tr className="border-b border-line/60">
              <td className="w-44 py-1.5 pr-2 font-semibold text-ink">Client</td>
              <td className="py-1.5 text-body">
                {clientName || "—"} <span className="text-muted">({clientTypeLabel})</span>
              </td>
            </tr>
            <tr className="border-b border-line/60">
              <td className="py-1.5 pr-2 font-semibold text-ink">Start date</td>
              <td className="py-1.5 text-body">{formatLong(startDate) || "—"}</td>
            </tr>
            <tr className="border-b border-line/60">
              <td className="py-1.5 pr-2 font-semibold text-ink">Expected duration</td>
              <td className="py-1.5 text-body">{duration || "—"}</td>
            </tr>
            <tr className="border-b border-line/60">
              <td className="py-1.5 pr-2 font-semibold text-ink">Max people on site</td>
              <td className="py-1.5 text-body">{maxPeople || "—"}</td>
            </tr>
            <tr className="border-b border-line/60">
              <td className="py-1.5 pr-2 font-semibold text-ink">Revision</td>
              <td className="py-1.5 text-body">
                {evidence.revision || "1"} · {formatLong(evidence.date) || "—"}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Management */}
        {management.some((m) => m.value.trim()) && (
          <section className="mt-5 border-t border-line pt-4">
            <h3 className="font-display text-base font-bold text-ink">Management of the work</h3>
            <dl className="mt-2 grid gap-x-4 gap-y-2 text-[0.88rem] sm:grid-cols-[200px_1fr]">
              {management
                .filter((m) => m.value.trim())
                .map((m) => (
                  <div key={m.label} className="contents">
                    <dt className="font-semibold text-ink">{m.label}</dt>
                    <dd className="whitespace-pre-line text-body">{m.value}</dd>
                  </div>
                ))}
            </dl>
          </section>
        )}

        {/* Arrangements */}
        {arrangements.some((a) => a.value.trim()) && (
          <section className="mt-5 border-t border-line pt-4">
            <h3 className="font-display text-base font-bold text-ink">Site arrangements</h3>
            <dl className="mt-2 grid gap-x-4 gap-y-2 text-[0.88rem] sm:grid-cols-[200px_1fr]">
              {arrangements
                .filter((a) => a.value.trim())
                .map((a) => (
                  <div key={a.label} className="contents">
                    <dt className="font-semibold text-ink">{a.label}</dt>
                    <dd className="whitespace-pre-line text-body">{a.value}</dd>
                  </div>
                ))}
            </dl>
          </section>
        )}

        {/* Key risks */}
        {selectedRisks.length > 0 && (
          <section className="mt-5 border-t border-line pt-4">
            <h3 className="font-display text-base font-bold text-ink">
              Key risks &amp; controls
            </h3>
            <div className="mt-2 space-y-3">
              {selectedRisks.map((r) => (
                <div key={r.id}>
                  <h4 className="text-[0.92rem] font-bold text-ink">{r.heading}</h4>
                  <p className="mt-0.5 text-[0.88rem] leading-relaxed text-body">{r.paragraph}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Emergency */}
        {emergency.some((e) => e.value.trim()) && (
          <section className="mt-5 border-t border-line pt-4">
            <h3 className="font-display text-base font-bold text-ink">Emergency arrangements</h3>
            <dl className="mt-2 grid gap-x-4 gap-y-2 text-[0.88rem] sm:grid-cols-[200px_1fr]">
              {emergency
                .filter((e) => e.value.trim())
                .map((e) => (
                  <div key={e.label} className="contents">
                    <dt className="font-semibold text-ink">{e.label}</dt>
                    <dd className="whitespace-pre-line text-body">{e.value}</dd>
                  </div>
                ))}
            </dl>
          </section>
        )}

        <p className="mt-5 text-[0.78rem] leading-relaxed text-muted">
          This builder structures a construction phase plan; whether it is suitable, sufficient and
          proportionate to this project is a matter for the competent person responsible for the work. It does
          not certify CDM 2015 compliance. Keep the plan on site, follow it, and update it as the work changes.
        </p>

        <SitePlanPrint value={sitePlan} heading="Site layout sketch" />

        <EvidencePrint value={evidence} briefing />

        <PrintFooter toolName="construction phase plan builder" />
      </div>
    </div>
  );
}
